import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { Link, useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useMediaQuery';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Check if user exists in database
  const checkUserExists = async (email) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', email)
      .maybeSingle();
    return !!data;
  };

  // Google OAuth login
  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'https://wwmmcgc.pages.dev/auth-callback', // Special callback page
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
          },
        },
      });

      if (error) throw error;
    } catch (err) {
      console.error('Google login error:', err);
      setMessage('❌ Google login failed. Please try again.');
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          // Check if email exists to give better error
          const userExists = await checkUserExists(email);
          if (!userExists) {
            setMessage('❌ No account found. Please sign up first.');
            setTimeout(() => navigate('/signup'), 2000);
          } else {
            setMessage('❌ Invalid email or password. Please try again.');
          }
        } else if (error.message.includes('Email not confirmed')) {
          setMessage('⚠️ Please confirm your email address before logging in.');
        } else if (error.message.includes('rate limit')) {
          setMessage('⏱️ Too many attempts. Please wait a few minutes.');
        } else {
          setMessage(`❌ ${error.message}`);
        }
        setLoading(false);
        return;
      }

      if (data.user) {
        // Check if profile is complete
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', data.user.id)
          .maybeSingle();

        if (!profile) {
          // User exists but no profile - send to complete signup
          setMessage('⚠️ Please complete your profile first.');
          setTimeout(() => navigate('/personal-info'), 1500);
          return;
        }

        if (rememberMe) {
          await supabase.auth.updateUser({
            data: { remember_me: true }
          });
        }
        
        navigate('/home');
      }
    } catch (err) {
      console.error('Login error:', err);
      setMessage('❌ Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setMessage('❌ Please enter your email address first.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://wwmmcgc.pages.dev/reset-password',
      });

      if (error) {
        if (error.message.includes('rate limit') || error.message.includes('security')) {
          setMessage('⏱️ Too many reset attempts. Please wait 60 seconds and try again.');
        } else {
          setMessage(`❌ ${error.message}`);
        }
        setLoading(false);
        return;
      }

      setResetSent(true);
      setMessage('✅ Password reset email sent! Check your inbox.');
    } catch (err) {
      console.error('Reset error:', err);
      setMessage('❌ Failed to send reset email. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={getContainerStyle(isMobile)}>
      <button onClick={() => navigate('/')} style={getBackButtonStyle(isMobile)}>
        ←
      </button>

      <div style={getContentStyle(isMobile)}>
        <h1 style={getTitleStyle(isMobile)}>Welcome Back</h1>
        <p style={getSubtitleStyle(isMobile)}>Log in to your account</p>

        {/* Google Login Button */}
        <button onClick={handleGoogleLogin} disabled={loading} style={getGoogleButtonStyle(isMobile)}>
          <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '12px' }}>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div style={dividerStyle}>
          <span style={dividerLineStyle}></span>
          <span style={dividerTextStyle}>or</span>
          <span style={dividerLineStyle}></span>
        </div>

        <form onSubmit={handleLogin} style={getFormStyle(isMobile)}>
          <div style={inputGroupStyle}>
            <label style={inputLabelStyle}>Email Address</label>
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => setEmail(e.target.value.toLowerCase().trim())}
              required
              style={getInputStyle(isMobile)}
              autoFocus
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={inputLabelStyle}>Password</label>
            <div style={passwordInputContainerStyle}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={{...getInputStyle(isMobile), paddingRight: '50px'}}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={eyeButtonStyle}>
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div style={rememberForgotRowStyle}>
            <label style={rememberMeContainerStyle}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={checkboxStyle}
              />
              <span style={rememberMeTextStyle}>Remember me</span>
            </label>
            
            <button
              type="button"
              onClick={handlePasswordReset}
              disabled={loading || resetSent}
              style={forgotPasswordStyle}
            >
              {resetSent ? 'Email sent!' : 'Forgot password?'}
            </button>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            style={loading ? {...getLoginButtonStyle(isMobile), ...buttonDisabledStyle} : getLoginButtonStyle(isMobile)}
          >
            {loading ? 'Logging in...' : 'Log In'}
          </button>
        </form>

        {message && (
          <div style={message.includes('✅') ? successMessageStyle : errorMessageStyle}>
            {message}
          </div>
        )}

        <p style={getFooterTextStyle(isMobile)}>
          Don't have an account?{' '}
          <Link to="/signup" style={linkStyle}>Sign Up</Link>
        </p>
      </div>
    </div>
  );
}

// Styles (same as before)
const passwordInputContainerStyle = { position: 'relative', width: '100%' };
const eyeButtonStyle = { position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '18px', padding: '8px', minWidth: '36px', minHeight: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const rememberForgotRowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '5px' };
const rememberMeContainerStyle = { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' };
const checkboxStyle = { width: '18px', height: '18px', cursor: 'pointer', accentColor: '#5865f2' };
const rememberMeTextStyle = { color: '#b5bac1', fontSize: '14px', userSelect: 'none' };
const forgotPasswordStyle = { background: 'transparent', border: 'none', color: '#00a8fc', fontSize: '14px', cursor: 'pointer', textDecoration: 'none', fontWeight: '600', padding: '0' };
const getGoogleButtonStyle = (isMobile) => ({ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: isMobile ? '14px' : '12px', borderRadius: '12px', border: '1px solid #dadce0', background: '#fff', color: '#3c4043', fontSize: isMobile ? '16px' : '14px', fontWeight: '500', cursor: 'pointer', minHeight: '48px', transition: 'all 0.2s', marginBottom: '20px' });
const dividerStyle = { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' };
const dividerLineStyle = { flex: 1, height: '1px', background: '#404249' };
const dividerTextStyle = { color: '#72767d', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600' };
const getContainerStyle = (isMobile) => ({ minHeight: '100vh', background: '#1a1b1e', display: 'flex', flexDirection: 'column', padding: isMobile ? '16px' : '20px', position: 'relative' });
const getBackButtonStyle = (isMobile) => ({ position: 'absolute', top: isMobile ? '16px' : '20px', left: isMobile ? '16px' : '20px', background: 'transparent', border: 'none', color: '#fff', fontSize: isMobile ? '28px' : '24px', cursor: 'pointer', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', zIndex: 10 });
const getContentStyle = (isMobile) => ({ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: '400px', width: '100%', margin: '0 auto', paddingTop: isMobile ? '80px' : '60px' });
const getTitleStyle = (isMobile) => ({ color: '#fff', fontSize: isMobile ? '28px' : '24px', fontWeight: 'bold', margin: '0 0 12px 0', textAlign: 'center' });
const getSubtitleStyle = (isMobile) => ({ color: '#b5bac1', fontSize: isMobile ? '15px' : '14px', margin: '0 0 30px 0', textAlign: 'center', lineHeight: '1.5' });
const getFormStyle = (isMobile) => ({ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '20px', width: '100%' });
const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: '8px' };
const inputLabelStyle = { color: '#b5bac1', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' };
const getInputStyle = (isMobile) => ({ padding: isMobile ? '16px' : '12px 16px', borderRadius: '12px', border: 'none', background: '#1e1f22', color: '#fff', fontSize: '16px', outline: 'none', minHeight: '48px', width: '100%', boxSizing: 'border-box' });
const getLoginButtonStyle = (isMobile) => ({ padding: isMobile ? '16px' : '14px', borderRadius: '12px', border: 'none', background: '#5865f2', color: '#fff', fontSize: isMobile ? '17px' : '16px', fontWeight: '600', cursor: 'pointer', marginTop: '10px', minHeight: '52px', width: '100%', transition: 'all 0.2s' });
const buttonDisabledStyle = { opacity: 0.5, cursor: 'not-allowed' };
const successMessageStyle = { marginTop: '20px', padding: '14px', borderRadius: '10px', background: 'rgba(35, 165, 90, 0.1)', border: '1px solid rgba(35, 165, 90, 0.3)', color: '#23a55a', fontSize: '14px', textAlign: 'center', lineHeight: '1.5' };
const errorMessageStyle = { marginTop: '20px', padding: '14px', borderRadius: '10px', background: 'rgba(242, 63, 66, 0.1)', border: '1px solid rgba(242, 63, 66, 0.3)', color: '#f23f42', fontSize: '14px', textAlign: 'center', lineHeight: '1.5' };
const getFooterTextStyle = (isMobile) => ({ color: '#72767d', fontSize: isMobile ? '15px' : '14px', textAlign: 'center', marginTop: isMobile ? '30px' : '20px', lineHeight: '1.6' });
const linkStyle = { color: '#00a8fc', textDecoration: 'none', fontWeight: '600' };